import { useMemo, useState } from 'react';
import { Building2, ChevronDown, CreditCard, Download, Loader2, Search } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Pagination, usePagination } from '../components/Pagination';
import { DateRangeFilter, inRange, ALL_TIME, type DateRange } from '../components/DateRangeFilter';
import { InvoiceStatusBadge, isPayableInvoiceStatus, providerLabel } from '../components/InvoiceStatusBadge';
import { Money } from '../components/Money';
import { PageHeader } from '../components/PageHeader';
import { Input } from '../components/Input';
import { cn } from '../lib/cn';
import { formatDate } from '../lib/format';
import { downloadCsv } from '../lib/csv';
import { toMajor } from '../contracts/money';
import { payInvoices } from '../api/billing';
import { usePaymentConsent } from '../components/PolicyConsent';
import { useBusinesses, useInvoices, useActiveBusiness } from '../api/hooks';

type ScopeKey = 'current' | 'all' | string; // 'current' | 'all' | <businessId>

export function Billing() {
  // Policy consent is required before any hand-off to the payment gateway.
  const consent = usePaymentConsent();
  const { data: businessesData } = useBusinesses();
  const { data: invoicesData } = useInvoices();
  const { data: currentBusiness } = useActiveBusiness();
  const allBusinesses = businessesData ?? [];
  const allInvoices = invoicesData ?? [];

  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<ScopeKey>('current');
  const [scopeOpen, setScopeOpen] = useState(false);

  // Deps MUST include the data. With only [scope] this memoised the FIRST render —
  // when the invoice query was still loading and allInvoices was [] — and never
  // recomputed, so the table stayed empty and every money KPI read ৳0 forever. An owner
  // being dunned saw "Outstanding ৳0" while the scope dropdown beside it, reading
  // allInvoices.length directly, showed the real count.
  const scopedInvoices = useMemo(() => {
    if (scope === 'all') return allInvoices;
    if (scope === 'current') return allInvoices.filter((i) => i.businessId === currentBusiness?.id);
    return allInvoices.filter((i) => i.businessId === scope);
  }, [scope, allInvoices, currentBusiness]);

  // #11 — filters on the invoice date.
  const [dateRange, setDateRange] = useState<DateRange>(ALL_TIME);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopedInvoices.filter(
      (i) =>
        inRange(i.date, dateRange) &&
        (!q || i.number.toLowerCase().includes(q) || providerLabel(i.method).toLowerCase().includes(q)),
    );
  }, [scopedInvoices, search, dateRange]);

  // Paginates the FILTERED list, so the CSV export above still writes every
  // matching invoice rather than the visible page (#12).
  const pager = usePagination(filtered);

  const currentYear = String(new Date().getFullYear());
  const totalYtd = scopedInvoices
    .filter((i) => i.status === 'paid' && i.date.startsWith(currentYear))
    .reduce((s, i) => s + i.amount, 0);
  const lastPaid = scopedInvoices.find((i) => i.status === 'paid');
  // Outstanding = issued-but-unpaid invoices (open / past_due / uncollectible).
  const unpaidInvoices = scopedInvoices.filter(
    (i) => i.status === 'open' || i.status === 'past_due' || i.status === 'uncollectible',
  );
  const outstanding = unpaidInvoices.reduce((s, i) => s + i.amount, 0);
  const isAll = scope === 'all';

  // Multi-select pay — one business per transaction (selecting from another business resets).
  const [selection, setSelection] = useState<{ businessId: string; ids: string[] }>({ businessId: '', ids: [] });
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const selectedInvoices = allInvoices.filter((i) => selection.ids.includes(i.id));
  const selectedTotal = selectedInvoices.reduce((s, i) => s + i.amount, 0);
  const hasPayable = filtered.some((i) => isPayableInvoiceStatus(i.status));

  function toggleSelect(inv: (typeof filtered)[number]) {
    setSelection((s) => {
      if (s.businessId !== inv.businessId) return { businessId: inv.businessId, ids: [inv.id] }; // switch business
      const has = s.ids.includes(inv.id);
      return { businessId: inv.businessId, ids: has ? s.ids.filter((x) => x !== inv.id) : [...s.ids, inv.id] };
    });
  }

  async function pay(businessId: string, invoiceIds: string[]) {
    if (paying || invoiceIds.length === 0) return;
    setPaying(true);
    setPayError(null);
    try {
      const { gatewayPageURL } = await payInvoices({ businessId, invoiceIds });
      if (gatewayPageURL) window.location.href = gatewayPageURL;
      else setPaying(false);
    } catch (e) {
      setPayError(e instanceof Error ? e.message : 'Could not start the payment. Please try again.');
      setPaying(false);
    }
  }

  const scopeLabel =
    scope === 'all'
      ? 'All businesses'
      : scope === 'current'
      ? currentBusiness?.name ?? 'This business'
      : allBusinesses.find((b) => b.id === scope)?.name ?? 'Unknown';

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow={isAll ? 'Account · all businesses' : `Invoices for ${scopeLabel}`}
        title={
          isAll ? (
            <span className="flex items-center gap-3 flex-wrap">
              All invoices
              <span className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
                {allBusinesses.length} businesses
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-3 flex-wrap">
              <span className={cn('w-9 h-9 rounded-md grid place-items-center flex-shrink-0', currentBusiness?.iconColor)}>
                <Building2 className="w-5 h-5 text-white dark:text-zinc-950" />
              </span>
              <span>Invoices</span>
              <span className="text-base font-normal text-zinc-500 dark:text-zinc-400">
                · {scopeLabel}
              </span>
            </span>
          )
        }
        subtitle={
          isAll
            ? 'Every subscription invoice across all your businesses.'
            : 'Subscription invoices for this business. Each business has its own billing.'
        }
        action={
          <>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Download className="w-4 h-4" />}
              disabled={filtered.length === 0}
              onClick={() =>
                downloadCsv(
                  'invoices',
                  ['Invoice', 'Business', 'Date', 'Amount (BDT)', 'Method', 'Status'],
                  filtered.map((inv) => {
                    const biz = allBusinesses.find((b) => b.id === inv.businessId);
                    return [inv.number, biz?.name ?? inv.businessId, formatDate(inv.date), toMajor(inv.amount), providerLabel(inv.method), inv.status];
                  }),
                )
              }
            >
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          </>
        }
      />

      {/* Scope picker — current business / all / specific other business */}
      <div className="mb-5 relative">
        <button
          type="button"
          onClick={() => setScopeOpen((s) => !s)}
          aria-haspopup="menu"
          aria-expanded={scopeOpen}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 transition"
        >
          <Building2 className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
          <span className="text-zinc-700 dark:text-zinc-200">
            <span className="text-zinc-500 dark:text-zinc-400">Showing: </span>
            <span className="font-semibold text-zinc-950 dark:text-zinc-50">{scopeLabel}</span>
          </span>
          <ChevronDown className={cn('w-3.5 h-3.5 text-zinc-400 transition-transform', scopeOpen && 'rotate-180')} />
        </button>

        {scopeOpen && (
          <div
            role="menu"
            className="absolute left-0 top-full mt-1.5 z-30 w-72 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-lg overflow-hidden"
          >
            <ScopeOption
              label="All businesses"
              hint={`Combined view · ${allInvoices.length} invoices`}
              selected={scope === 'all'}
              onClick={() => {
                setScope('all');
                setScopeOpen(false);
              }}
              icon={<Building2 className="w-4 h-4" />}
            />
            <div className="border-t border-zinc-200 dark:border-zinc-700">
              <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Single business
              </div>
              {allBusinesses.map((b) => {
                const isSelected =
                  (scope === 'current' && b.id === currentBusiness?.id) || scope === b.id;
                const count = allInvoices.filter((i) => i.businessId === b.id).length;
                return (
                  <ScopeOption
                    key={b.id}
                    label={b.name}
                    hint={`${count} invoice${count === 1 ? '' : 's'}`}
                    selected={isSelected}
                    onClick={() => {
                      setScope(b.id === currentBusiness?.id ? 'current' : b.id);
                      setScopeOpen(false);
                    }}
                    avatarName={b.name}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <KpiTile
          label={isAll ? 'Total billed (YTD, all)' : 'Total billed (YTD)'}
          value={
            <Money minor={totalYtd} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
          }
        />
        <KpiTile
          label="Outstanding"
          value={
            <Money
              minor={outstanding}
              hideCents
              tone="neutral"
              className={outstanding > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-zinc-950 dark:text-zinc-50'}
            />
          }
          subtitle={
            outstanding > 0
              ? `${unpaidInvoices.length} unpaid invoice${unpaidInvoices.length === 1 ? '' : 's'}`
              : 'No unpaid invoices'
          }
        />
        <KpiTile
          label="Last paid"
          value={
            lastPaid ? (
              <Money minor={lastPaid.amount} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
            ) : (
              <span className="text-zinc-400 dark:text-zinc-500">—</span>
            )
          }
          subtitle={lastPaid ? `On ${formatDate(lastPaid.date)}` : undefined}
        />
        <KpiTile
          label="Total invoices"
          value={
            <span className="text-zinc-950 dark:text-zinc-50 tabular-nums">{scopedInvoices.length}</span>
          }
          subtitle={isAll ? 'across all businesses' : `for ${scopeLabel}`}
        />
      </div>

      <Card className="mb-4">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <DateRangeFilter label="Date" value={dateRange} onChange={setDateRange} />
          <div className="ml-auto w-full sm:w-64">
            <Input
              placeholder="Search invoice #"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                {hasPayable && <th className="w-10 pl-5 sm:pl-6 pr-1 py-3"></th>}
                <th className="text-left font-medium px-5 sm:px-6 py-3">Invoice</th>
                {isAll && <th className="text-left font-medium px-4 py-3">Business</th>}
                <th className="text-left font-medium px-4 py-3">Date</th>
                <th className="text-right font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Method</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={isAll ? 7 : 6} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No invoices match your filters.
                  </td>
                </tr>
              ) : (
                pager.pageItems.map((inv) => {
                  const biz = allBusinesses.find((b) => b.id === inv.businessId);
                  const payable = isPayableInvoiceStatus(inv.status);
                  return (
                    <tr key={inv.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                      {hasPayable && (
                        <td className="pl-5 sm:pl-6 pr-1 py-4">
                          {payable && (
                            <input
                              type="checkbox"
                              checked={selection.ids.includes(inv.id)}
                              onChange={() => toggleSelect(inv)}
                              aria-label={`Select invoice ${inv.number} to pay`}
                              className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                            />
                          )}
                        </td>
                      )}
                      <td className="px-5 sm:px-6 py-4 mono text-xs text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                        <Link
                          to="/billing/$id"
                          params={{ id: inv.id }}
                          className="hover:text-brand dark:hover:text-brand-dm"
                        >
                          {inv.number}
                        </Link>
                      </td>
                      {isAll && (
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2 min-w-0">
                            {biz && <Avatar name={biz.name} size="sm" />}
                            <span className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                              {biz?.name ?? '—'}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200 whitespace-nowrap tabular-nums">
                        {formatDate(inv.date)}
                      </td>
                      <td className="px-4 py-4 text-right text-zinc-950 dark:text-zinc-50">
                        <Money minor={inv.amount} tone="neutral" />
                      </td>
                      <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden sm:table-cell">
                        {providerLabel(inv.method)}
                      </td>
                      <td className="px-4 py-4">
                        <InvoiceStatusBadge status={inv.status} />
                      </td>
                      <td className="px-5 sm:px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          {payable && (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={paying}
                              onClick={() => consent.request(() => pay(inv.businessId, [inv.id]))}
                              iconLeft={<CreditCard className="w-3.5 h-3.5" />}
                            >
                              Pay
                            </Button>
                          )}
                          <Link
                            to="/billing/$id"
                            params={{ id: inv.id }}
                            className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition inline-flex"
                            aria-label={`Open invoice ${inv.number} (print/PDF)`}
                            title="Open invoice (print/PDF)"
                          >
                            <Download className="w-4 h-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination state={pager} label="invoices" />
      </Card>

      {payError && (
        <p className="mb-4 text-sm text-rose-600 dark:text-rose-400" role="alert">{payError}</p>
      )}

      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        Each business has its own subscription and its own invoices. Switch the scope above to see
        a different business, or pick "All businesses" to combine them for your accountant.
      </p>

      {/* Sticky pay-selected bar — one business per transaction (SSLCommerz). */}
      {selection.ids.length > 0 && (
        <div className="sticky bottom-4 z-20 mt-4 flex justify-center">
          <div className="flex items-center gap-4 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white/95 dark:bg-zinc-900/95 backdrop-blur px-4 py-2.5 shadow-lg">
            <span className="text-sm text-zinc-700 dark:text-zinc-200">
              <span className="font-semibold text-zinc-950 dark:text-zinc-50">{selection.ids.length}</span> selected ·{' '}
              <Money minor={selectedTotal} hideCents tone="neutral" className="font-semibold text-zinc-950 dark:text-zinc-50" />
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelection({ businessId: '', ids: [] })} disabled={paying}>
                Clear
              </Button>
              <Button
                size="sm"
                loading={paying}
                onClick={() => consent.request(() => pay(selection.businessId, selection.ids))}
                iconLeft={paying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
              >
                Pay {selection.ids.length} selected
              </Button>
            </div>
          </div>
        </div>
      )}
      {consent.dialog}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function KpiTile({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <Card className="p-4 sm:p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        {label}
      </div>
      <div className="mt-1.5 text-xl sm:text-2xl font-semibold">{value}</div>
      {subtitle && (
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
      )}
    </Card>
  );
}

function ScopeOption({
  label,
  hint,
  selected,
  onClick,
  icon,
  avatarName,
}: {
  label: string;
  hint?: string;
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  avatarName?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 transition text-left',
        selected ? 'bg-brand-subtle dark:bg-brand-dm-subtle/40' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
      )}
    >
      <span className="w-7 h-7 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0">
        {avatarName ? <Avatar name={avatarName} size="sm" /> : icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{label}</div>
        {hint && (
          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate tabular-nums">{hint}</div>
        )}
      </div>
      {selected && (
        <span className="w-1.5 h-1.5 rounded-full bg-brand dark:bg-brand-dm flex-shrink-0" />
      )}
    </button>
  );
}
